import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, effect, inject, input, signal } from '@angular/core';

import { RouterModule } from '@angular/router';
import { LiveListenButtonComponent } from '@shared/components/live-listen-button/live-listen-button.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TabsModule } from 'primeng/tabs';
import { Menu, MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';

import { TestsService } from '@generated/services/tests.service';
import { AgentsService } from '@generated/services/agents.service';
import { AgentTestDto, TestSummary, TestType, TestRunSummary, TestRunSessionDto, TestStatus } from '@generated/models';
import { NotificationService } from '@core/services/notification.service';
import { Signalr } from '@core/services/signalr';
import { AssistantAnchorDirective } from '@shared/directives/assistant-anchor.directive';

interface AttachedRow {
  agentTestId: string;
  testId: string;
  test?: TestSummary;
}

@Component({
  selector: 'app-agent-tests-tab',
  standalone: true,
  imports: [
    LiveListenButtonComponent,
    RouterModule,
    ButtonModule,
    TableModule,
    TagModule,
    TooltipModule,
    TabsModule,
    MenuModule,
    AssistantAnchorDirective
],
  templateUrl: './agent-tests-tab.component.html',
  styleUrl: './agent-tests-tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentTestsTabComponent implements OnInit {
  private testsService = inject(TestsService);
  private agentsService = inject(AgentsService);
  private notificationService = inject(NotificationService);
  private signalr = inject(Signalr);
  private destroyRef = inject(DestroyRef);

  readonly agentId = input.required<string>();

  innerTab = signal<'attached' | 'runs'>('attached');

  // Attached tests
  attachedLoading = signal(false);
  private agentTests = signal<AgentTestDto[]>([]);
  private allTests = signal<TestSummary[]>([]);
  isRunningAll = signal(false);
  isAdding = signal(false);
  /** agent_test id of the row whose single-test run is in flight. */
  runningRowId = signal<string | null>(null);

  private testsById = computed(() => {
    const map = new Map<string, TestSummary>();
    for (const t of this.allTests()) if (t.id) map.set(t.id, t);
    return map;
  });

  readonly attachedRows = computed<AttachedRow[]>(() =>
    this.agentTests().map(at => ({
      agentTestId: at.id ?? '',
      testId: at.test_id ?? '',
      test: at.test_id ? this.testsById().get(at.test_id) : undefined,
    })),
  );

  /** "Add test" menu — every existing test, click attaches it (duplicates allowed). */
  readonly addMenuItems = computed<MenuItem[]>(() =>
    this.allTests().map(t => ({
      label: t.name || $localize`:@@agentTests.unnamed:Unnamed test`,
      icon: 'pi pi-file',
      command: () => this.attachTest(t.id!),
    })),
  );

  // Past test runs
  runsLoading = signal(false);
  testRuns = signal<TestRunSummary[]>([]);
  private runsLoaded = false;

  expandedRows = signal<Record<string, boolean>>({});
  runSessions = signal<Record<string, TestRunSessionDto[]>>({});
  runSessionsLoading = signal<Record<string, boolean>>({});

  constructor() {
    // Lazily load runs the first time the user opens the runs sub-tab.
    effect(() => {
      if (this.innerTab() === 'runs' && !this.runsLoaded) {
        this.runsLoaded = true;
        this.loadRuns();
      }
    });
  }

  ngOnInit(): void {
    this.loadAttached();

    this.signalr.testCreated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reloadAllTests());

    this.signalr.testRunUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((run) => {
        if (!run?.id || run.agent_id !== this.agentId()) return;
        this.testRuns.update(list =>
          list.some(r => r.id === run.id)
            ? list.map(r => r.id === run.id ? { ...r, ...run } : r)
            : [run, ...list],
        );
      });

    this.signalr.agentSessionUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((session) => {
        const runId = session?.test_run_id;
        if (!session?.id || !runId) return;
        this.runSessions.update(map => {
          const bucket = map[runId];
          if (!bucket || !bucket.some(s => s.id === session.id)) return map;
          return { ...map, [runId]: bucket.map(s => s.id === session.id ? { ...s, ...session } : s) };
        });
      });
  }

  // ---- Attached tests ----

  private loadAttached(): void {
    this.attachedLoading.set(true);
    forkJoin({
      agentTests: this.testsService.apiAgentsAgentIdTestsGet({ agentId: this.agentId() }),
      allTests: this.testsService.apiTestsGet({}),
    }).subscribe({
      next: ({ agentTests, allTests }) => {
        this.agentTests.set(agentTests.agent_tests ?? []);
        this.allTests.set(allTests.tests ?? []);
        this.attachedLoading.set(false);
      },
      error: () => {
        this.notificationService.showError($localize`:@@agentTests.notify.loadFail:Failed to load tests`);
        this.attachedLoading.set(false);
      },
    });
  }

  private reloadAllTests(): void {
    this.testsService.apiTestsGet({}).subscribe({
      next: (res) => this.allTests.set(res.tests ?? []),
      error: () => {},
    });
  }

  openAddMenu(menu: Menu, event: Event): void {
    menu.toggle(event);
  }

  /** Current full set of attached test ids, in order (duplicates allowed). */
  private currentTestIds(): string[] {
    return this.agentTests().map(at => at.test_id).filter((id): id is string => !!id);
  }

  /** Сколько тестов прикреплено к агенту — встроенный агент сверяется
   *  с этим, прежде чем предлагать прогон. */
  readonly attachedTestCount = computed(() => this.agentTests().length);

  /** POST endpoint replaces the whole set — always send the full list. */
  private saveTestSet(testIds: string[], successMsg: string, done?: () => void): void {
    this.testsService.apiAgentsAgentIdTestsPost({
      agentId: this.agentId(),
      body: { test_ids: testIds },
    }).subscribe({
      next: () => {
        done?.();
        this.notificationService.showSuccess(successMsg);
        this.loadAttached();
      },
      error: () => {
        done?.();
        this.notificationService.showError($localize`:@@agentTests.notify.saveFail:Failed to update tests`);
      },
    });
  }

  /** Имена прикреплённых тестов — ими встроенный агент и оперирует. */
  readonly attachedTestNames = computed(() => {
    const byId = new Map(this.allTests().map(test => [test.id, test.name ?? test.id ?? '']));
    return this.agentTests()
      .map(at => byId.get(at.test_id ?? '') ?? at.test_id ?? '')
      .filter(Boolean);
  });

  /** Все тесты спейса по именам — что вообще можно прикрепить. */
  allTestNames(): string[] {
    return this.allTests().map(test => test.name ?? test.id ?? '').filter(Boolean);
  }

  /** Имена, которых в спейсе нет: модель должна узнать об этом словами. */
  unknownTestNames(names: readonly string[]): string[] {
    const known = new Set(this.allTestNames().map(name => name.toLowerCase()));
    return names.filter(name => !known.has(name.toLowerCase()));
  }

  /** Прикрепляет ровно перечисленные тесты — endpoint пишет весь набор сразу. */
  setAttachedByNames(names: readonly string[]): void {
    const byName = new Map(
      this.allTests().map(test => [(test.name ?? test.id ?? '').toLowerCase(), test.id ?? '']),
    );
    const ids = names.map(name => byName.get(name.toLowerCase()) ?? '').filter(Boolean);
    this.saveTestSet(ids, $localize`:@@agentTests.notify.attached:Test added to the agent`);
  }

  attachTest(testId: string): void {
    this.isAdding.set(true);
    this.saveTestSet(
      [...this.currentTestIds(), testId],
      $localize`:@@agentTests.notify.attached:Test added to the agent`,
      () => this.isAdding.set(false),
    );
  }

  removeRow(row: AttachedRow): void {
    // Remove this specific attachment occurrence by its agent_test id,
    // then resend the remaining set (duplicates of the same test_id are kept).
    const remaining = this.agentTests()
      .filter(at => at.id !== row.agentTestId)
      .map(at => at.test_id)
      .filter((id): id is string => !!id);
    this.saveTestSet(
      remaining,
      $localize`:@@agentTests.notify.removed:Test removed from the agent`,
    );
  }

  runOne(row: AttachedRow): void {
    if (!row.testId) return;
    this.runningRowId.set(row.agentTestId);
    this.runTests([row.testId], () => this.runningRowId.set(null));
  }

  runAll(): void {
    const ids = this.currentTestIds();
    if (ids.length === 0) {
      this.notificationService.showWarning($localize`:@@agentTests.notify.nothingToRun:No attached tests to run`);
      return;
    }
    this.isRunningAll.set(true);
    this.runTests(ids, () => this.isRunningAll.set(false));
  }

  private runTests(testIds?: string[], done?: () => void): void {
    this.testsService.apiTestRunsPost({
      body: { agent_id: this.agentId(), ...(testIds ? { test_ids: testIds } : {}) },
    }).subscribe({
      next: (result) => {
        done?.();
        const failed = result.failures?.length ?? 0;
        if (failed > 0) {
          this.notificationService.showWarning($localize`:@@agentTests.notify.partial:Test run started with ${failed}:count: failure(s)`);
        } else {
          this.notificationService.showSuccess($localize`:@@agentTests.notify.runStarted:Test run started`);
        }
        this.innerTab.set('runs');
        this.loadRuns();
      },
      error: () => {
        done?.();
        this.notificationService.showError($localize`:@@agentTests.notify.runFail:Failed to start test run`);
      },
    });
  }

  // ---- Past test runs ----

  loadRuns(): void {
    this.runsLoading.set(true);
    this.testsService.apiTestRunsGet({ agentId: this.agentId(), limit: 100 }).subscribe({
      next: (result) => {
        this.testRuns.set(result.items ?? []);
        this.runsLoading.set(false);
      },
      error: () => {
        this.notificationService.showError($localize`:@@tests.runs.notify.loadFail:Failed to load test runs`);
        this.runsLoading.set(false);
      },
    });
  }

  toggleRunExpansion(run: TestRunSummary): void {
    const id = run.id;
    if (!id) return;

    if (this.expandedRows()[id]) {
      this.expandedRows.update(m => {
        const next = { ...m };
        delete next[id];
        return next;
      });
      return;
    }

    this.expandedRows.update(m => ({ ...m, [id]: true }));

    if (this.runSessions()[id] || this.runSessionsLoading()[id]) return;
    this.runSessionsLoading.update(m => ({ ...m, [id]: true }));
    this.testsService.apiTestRunsIdGet({ id }).subscribe({
      next: (result) => {
        this.runSessions.update(m => ({ ...m, [id]: result.sessions ?? [] }));
        this.runSessionsLoading.update(m => ({ ...m, [id]: false }));
      },
      error: () => {
        this.notificationService.showError($localize`:@@tests.runs.notify.sessionsFail:Failed to load test run sessions`);
        this.runSessionsLoading.update(m => ({ ...m, [id]: false }));
        this.expandedRows.update(m => {
          const next = { ...m };
          delete next[id];
          return next;
        });
      },
    });
  }

  sessionsFor(runId: string): TestRunSessionDto[] {
    return this.runSessions()[runId] ?? [];
  }

  // ---- Helpers ----

  typeLabel(type?: TestType): string {
    switch (type) {
      case 'Simulation': return $localize`:@@tests.type.simulation:Simulation`;
      case 'E2E': return $localize`:@@tests.type.e2e.short:E2E`;
      default: return '-';
    }
  }

  testStatusSeverity(status?: TestStatus | null): 'success' | 'danger' | 'warn' | 'secondary' {
    switch (status) {
      case 'Success': return 'success';
      case 'Failed': return 'danger';
      case 'Processing': return 'warn';
      default: return 'secondary';
    }
  }

  testStatusLabel(status?: TestStatus | null): string {
    switch (status) {
      case 'Success': return $localize`:@@tests.runs.status.success:Success`;
      case 'Failed': return $localize`:@@tests.runs.status.failed:Failed`;
      case 'Processing': return $localize`:@@tests.runs.status.processing:Processing`;
      case 'Initial': return $localize`:@@tests.runs.status.initial:Initial`;
      default: return '-';
    }
  }

  runStatusSeverity(run: TestRunSummary): 'success' | 'danger' | 'warn' {
    if ((run.processing_sessions ?? 0) > 0) return 'warn';
    if ((run.failed_sessions ?? 0) > 0) return 'danger';
    return 'success';
  }

  runStatusLabel(run: TestRunSummary): string {
    const ok = run.success_sessions ?? 0;
    const total = run.total_sessions ?? 0;
    return `${ok}/${total}`;
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    });
  }
}
